/**
 * 实例详情抽屉（旧 components/UserInstanceDetail.vue）：基本信息、固定设定、风格提示、
 * 主题专属 Prompt（三者可编辑）、第一篇专属内容
 */
import { useState } from 'react'
import { Badge, Button, Descriptions, Divider, Drawer, Empty, Tag, Typography } from 'antd'
import { JsonViewer } from '@/components/json-viewer/JsonViewer'
import {
  coverageRate,
  formatDate,
  instanceStatusBadge,
  instanceStatusLabel,
  levelColor,
  levelLabel
} from './constants'
import { useInstanceDetail, useUpdateInstanceContent } from './hooks'
import { EditContentModal, type EditTarget } from './EditContentModal'
import type { UserInstance } from './types'
import styles from './UserInstances.module.css'

interface UserInstanceDetailProps {
  // 为 null 时抽屉关闭
  instance: UserInstance | null
  onClose: () => void
}

export function UserInstanceDetail({ instance: record, onClose }: Readonly<UserInstanceDetailProps>) {
  const { data: instance } = useInstanceDetail(record)
  const updateContent = useUpdateInstanceContent()
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null)

  const save = async (payload: Record<string, unknown>) => {
    if (!instance) return
    await updateContent.mutateAsync({ id: instance.id, payload })
  }

  const editFixedSetting = () =>
    instance &&
    setEditTarget({
      title: '编辑 Current Fixed Setting',
      mode: 'json',
      rows: 15,
      initialValue: JSON.stringify(instance.fixedSetting, null, 2),
      onSave: (value) => save({ currentFixedSetting: value })
    })

  const editStyleHints = () =>
    instance &&
    setEditTarget({
      title: '编辑 Style Hints',
      mode: 'json',
      rows: 15,
      initialValue: JSON.stringify(instance.styleHints, null, 2),
      onSave: (value) => save({ styleHints: value })
    })

  const editThemePrompt = () =>
    instance &&
    setEditTarget({
      title: '编辑主题专属 Prompt',
      mode: 'text',
      rows: 20,
      maxLength: 20000,
      placeholder: '请输入 Markdown 格式的主题专属 Prompt',
      hint: '💡 提示：支持 Markdown 格式，用于 AI 生成篇章时的额外指导',
      initialValue: instance.themeSpecificPrompt || '',
      onSave: (value) => save({ themeSpecificPrompt: value })
    })

  const editButton = (label: string, onClick: () => void) => (
    <Button type="link" size="small" aria-label={label} onClick={onClick}>
      编辑
    </Button>
  )

  return (
    <Drawer open={record !== null} title="实例详情" size={800} footer={null} onClose={onClose}>
      {instance && (
        <>
          <Descriptions column={2} bordered>
            <Descriptions.Item label="用户ID">{instance.userId}</Descriptions.Item>
            <Descriptions.Item label="用户名">{instance.userName || '-'}</Descriptions.Item>
            <Descriptions.Item label="主题">{instance.themeTitle}</Descriptions.Item>
            <Descriptions.Item label="主题代码">{instance.themeCode}</Descriptions.Item>
            <Descriptions.Item label="用户等级">
              <Tag color={levelColor(instance.userLevel)}>{levelLabel(instance.userLevel)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Badge status={instanceStatusBadge(instance.status)} text={instanceStatusLabel(instance.status)} />
            </Descriptions.Item>
            <Descriptions.Item label="篇章数量">{instance.episodeCount}</Descriptions.Item>
            <Descriptions.Item label="总词数">{instance.totalWordCount}</Descriptions.Item>
            <Descriptions.Item label="已知词数">{instance.knownWordCount}</Descriptions.Item>
            <Descriptions.Item label="覆盖率">{coverageRate(instance)}%</Descriptions.Item>
            <Descriptions.Item label="开始时间">{formatDate(instance.startedAt)}</Descriptions.Item>
            <Descriptions.Item label="最后活跃">{formatDate(instance.lastActiveAt)}</Descriptions.Item>
            <Descriptions.Item label="完成时间" span={2}>
              {instance.completedAt ? formatDate(instance.completedAt) : '-'}
            </Descriptions.Item>
          </Descriptions>

          <Divider>
            Current Fixed Setting (当前固定设定) {editButton('编辑固定设定', editFixedSetting)}
          </Divider>
          <JsonViewer data={instance.fixedSetting} />

          <Divider>
            风格提示 (Style Hints) {editButton('编辑风格提示', editStyleHints)}
          </Divider>
          <JsonViewer data={instance.styleHints} />

          <Divider>
            主题专属 Prompt 提示词 {editButton('编辑主题专属 Prompt', editThemePrompt)}
          </Divider>
          {instance.themeSpecificPrompt ? (
            <>
              <Typography.Paragraph
                className={styles.preWrap}
                ellipsis={{ rows: 10, expandable: true, symbol: '展开全部' }}
              >
                {instance.themeSpecificPrompt}
              </Typography.Paragraph>
              <div className={styles.promptNote}>
                <div>
                  <strong>💡 说明：</strong>此提示词从主题复制，用于 AI 生成篇章时的个性化指导
                </div>
                <Typography.Text type="secondary" className={styles.small}>
                  <strong>📝 字数：</strong>
                  {instance.themeSpecificPrompt.length} 字符
                </Typography.Text>
              </div>
            </>
          ) : (
            <Empty description="暂无主题专属 Prompt（该实例创建时主题未配置）" />
          )}

          <Divider>第一篇专属内容</Divider>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="第一篇标题">{instance.firstEpisodeTitle || '（未设置）'}</Descriptions.Item>
            <Descriptions.Item label="第一篇预告内容">
              {instance.firstEpisodeStoryText ? (
                <div className={styles.preWrap}>{instance.firstEpisodeStoryText}</div>
              ) : (
                <Typography.Text type="secondary">（未设置）</Typography.Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}

      <EditContentModal target={editTarget} onClose={() => setEditTarget(null)} />
    </Drawer>
  )
}
